import { detectOS } from '@/lib/platform';
import amplitudeService from '@/services/amplitude';
import { nativeApi } from '@/services/native-api/native-api';
import { useCallback, useEffect, useState } from 'react';
import type { PermissionState } from './types';

const MACOS_MIC_SETTINGS_URL =
  'x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone';
const WINDOWS_MIC_SETTINGS_URL = 'ms-settings:privacy-microphone';

const checkMicPermissionViaStream = async (): Promise<PermissionState> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    for (const track of stream.getTracks()) {
      track.stop();
    }
    return 'granted';
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      return 'denied';
    }
    return 'default';
  }
};

export const useMicrophonePermission = () => {
  const [hasMicrophonePermission, setHasMicrophonePermission] = useState<PermissionState | null>(
    null,
  );

  useEffect(() => {
    const checkPermission = async () => {
      if (nativeApi) {
        const state = await checkMicPermissionViaStream();
        setHasMicrophonePermission(state);
        return;
      }

      try {
        const result = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        setHasMicrophonePermission(result.state);

        result.onchange = () => {
          setHasMicrophonePermission(result.state);
        };
      } catch {
        // permissions.query can throw in cross-origin iframes (e.g. HubSpot).
        // Can't passively detect state without triggering a prompt, so default
        // to 'default' and let requestMicrophonePermission handle the actual prompt.
        setHasMicrophonePermission('default');
      }
    };

    checkPermission();

    // On desktop, re-check when the app regains focus (user may have toggled in System Settings)
    if (nativeApi) {
      let debounceTimer: ReturnType<typeof setTimeout>;
      const onFocus = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          checkMicPermissionViaStream().then(setHasMicrophonePermission);
        }, 500);
      };
      window.addEventListener('focus', onFocus);
      return () => {
        clearTimeout(debounceTimer);
        window.removeEventListener('focus', onFocus);
      };
    }
  }, []);

  const openMicSystemSettings = useCallback(() => {
    if (nativeApi) {
      const os = detectOS();
      const url = os === 'windows' ? WINDOWS_MIC_SETTINGS_URL : MACOS_MIC_SETTINGS_URL;
      window.desktopAPI?.openUrl.openExternal.callback(url);
    }
  }, []);

  const requestMicrophonePermission = async () => {
    try {
      amplitudeService.logEvent('Microphone Permission Troubleshooting Tapped', {
        Source: 'Onboarding Permission Screen, Desktop app',
      });

      // On desktop, if already denied at OS level, open system settings instead
      // because Electron can't re-prompt once macOS has denied the permission
      if (nativeApi && hasMicrophonePermission === 'denied') {
        openMicSystemSettings();
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      for (const track of stream.getTracks()) {
        track.stop();
      }

      setHasMicrophonePermission('granted');
    } catch (error) {
      console.error(error);
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setHasMicrophonePermission('denied');
      } else {
        setHasMicrophonePermission('default');
      }
    }
  };

  return { hasMicrophonePermission, requestMicrophonePermission, openMicSystemSettings };
};
